import posthog from "posthog-js";
import type { PostHog } from "posthog-js";
import type { PrivateUserProfileView } from "@bbb/model";

const DEFAULT_POSTHOG_HOST = "https://eu.i.posthog.com";

type AnalyticsProperties = Record<
  string,
  string | number | boolean | null | undefined
>;

let initialized = false;

function getProjectToken() {
  return import.meta.env.VITE_PUBLIC_POSTHOG_PROJECT_TOKEN?.trim() || "";
}

function getHost() {
  return (
    import.meta.env.VITE_PUBLIC_POSTHOG_HOST?.trim() ||
    import.meta.env.VITE_POSTHOG_HOST?.trim() ||
    DEFAULT_POSTHOG_HOST
  );
}

export function initPostHog(): PostHog | null {
  if (initialized) {
    return posthog;
  }

  const token = getProjectToken();
  if (!token) {
    return null;
  }

  posthog.init(token, {
    api_host: getHost(),
    autocapture: true,
    capture_pageview: true,
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: true,
    },
    disable_session_recording: true,
  });
  initialized = true;

  return posthog;
}

export function getPostHogClient(): PostHog | null {
  return initialized ? posthog : initPostHog();
}

export function trackUsageEvent(
  event: string,
  properties: AnalyticsProperties = {},
) {
  getPostHogClient()?.capture(event, properties);
}

export function identifyAnalyticsUser(
  user: Partial<
    Pick<
      PrivateUserProfileView,
      "id" | "userType" | "initialUserType" | "verified"
    >
  >,
  properties: AnalyticsProperties = {},
) {
  const distinctId = user.id?.trim();
  if (!distinctId) {
    return;
  }

  getPostHogClient()?.identify(distinctId, {
    user_type: user.userType ?? null,
    initial_user_type: user.initialUserType ?? null,
    verified: user.verified ?? false,
    ...properties,
  });
}

export function resetAnalyticsUser() {
  getPostHogClient()?.reset();
}

export function getPostHogRequestHeaders(): Record<string, string> {
  const client = getPostHogClient();
  if (!client) {
    return {};
  }

  const headers: Record<string, string> = {};
  const distinctId = client.get_distinct_id();
  const sessionId = (
    client as PostHog & { get_session_id?: () => string | null }
  ).get_session_id?.();

  if (distinctId) {
    headers["X-POSTHOG-DISTINCT-ID"] = distinctId;
  }

  if (sessionId) {
    headers["X-POSTHOG-SESSION-ID"] = sessionId;
  }

  return headers;
}
